import ClickableTableCell from 'Components/ClickableTableCell'
import CustomTablePagination from 'Components/CustomTablePagination'
import EnhancedTableHead, { type HeadCell } from 'Components/EnhancedTableHead/EnhancedTableHead'
import NoWrapTableCell from 'Components/NoWrapTableCell'
import StyledTableRow from 'Components/StyledTableRow'
import { WithLoader } from 'Components/WithLoader'
import type {
  FetchRefundCodeListParams,
  RefundCode
} from 'Services/ControlDataManagement/Features/RefundCode/types'
import { ROWS_PER_PAGE_DEFAULT, SORT_ORDER_ASC } from 'Util/constants'
import { datetimeFormatter, showValueOrHyphen } from 'Util/formatters'
import { Paper, Table, TableBody, TableContainer } from '@mui/material'
import { t } from 'ttag'

interface RefundCodeListProps {
  rows: RefundCode[]
  isLoading: boolean
  onRowClick: (id: string) => void
  selectedRefundCodeId: string | undefined
  params: FetchRefundCodeListParams
  handleRequestSort: (event: React.MouseEvent<unknown>, property: string) => void
  handleChangePage: (event: unknown, newPage: number) => void
  count: number
  page: number
}

export default function RefundCodeList({
  rows,
  isLoading,
  onRowClick,
  selectedRefundCodeId,
  params,
  handleRequestSort,
  handleChangePage,
  count,
  page
}: Readonly<RefundCodeListProps>) {
  const headCells: HeadCell[] = [
    {
      id: 'id',
      numeric: false,
      disablePadding: false,
      label: t`Identifier`,
      sortable: false
    },
    {
      id: 'refundCodeId',
      numeric: false,
      disablePadding: false,
      label: t`Refund code identifier`,
      sortable: false
    },
    {
      id: 'modifiedTimestamp',
      numeric: false,
      disablePadding: false,
      label: t`Modified`,
      sortable: false
    },
    {
      id: 'createdTimestamp',
      numeric: false,
      disablePadding: false,
      label: t`Created`,
      sortable: false
    }
  ]

  const orderPrefix = `${params.sort_order === SORT_ORDER_ASC ? '-' : ''}`
  const orderBy = `${orderPrefix}${params.sort_by}`

  return (
    <WithLoader loading={isLoading} rows={ROWS_PER_PAGE_DEFAULT} height={33}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="small">
            <EnhancedTableHead
              orderBy={orderBy}
              headCells={headCells}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {rows.map(row => {
                return (
                  <StyledTableRow
                    hover
                    tabIndex={-1}
                    key={row.id}
                    selected={selectedRefundCodeId === row.refund_code_id}
                  >
                    <ClickableTableCell onClick={() => onRowClick(row.refund_code_id)}>
                      {showValueOrHyphen(row.id)}
                    </ClickableTableCell>
                    <ClickableTableCell onClick={() => onRowClick(row.refund_code_id)}>
                      {showValueOrHyphen(row.refund_code_id)}
                    </ClickableTableCell>
                    <NoWrapTableCell
                      title={datetimeFormatter(row.modified_timestamp)}
                      component="th"
                      scope="row"
                    >
                      {datetimeFormatter(row.modified_timestamp)}
                    </NoWrapTableCell>
                    <NoWrapTableCell
                      title={datetimeFormatter(row.created_timestamp)}
                      component="th"
                      scope="row"
                    >
                      {datetimeFormatter(row.created_timestamp)}
                    </NoWrapTableCell>
                  </StyledTableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination count={count} page={page} onPageChange={handleChangePage} />
      </Paper>
    </WithLoader>
  )
}
